import React, { useState, useRef } from 'react';
import './StackQuest.css';
import { useProgress } from '../contexts/ProgressContext';
import { useDevMode } from '../contexts/DevModeContext';
import UnderTheHood from './UnderTheHood';

const StackQuest = () => {
  const [currentPhase, setCurrentPhase] = useState(1);
  const [programBlocks, setProgramBlocks] = useState([]);
  const [stack, setStack] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [executionSteps, setExecutionSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [randomNumbers, setRandomNumbers] = useState({});
  const hasInitialized = useRef(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const { updateGameProgress, getProgress } = useProgress();
  const { isDevMode } = useDevMode();

  // Educational explanations for each phase - now with ZK/Privacy context!
  const phaseExplanations = {
    1: {
      title: "Understanding Stack Operations",
      content: "A stack is a Last-In-First-Out (LIFO) data structure. Miden VM is a stack-based zero-knowledge virtual machine - every computation you perform here can be proven without revealing the inputs!",
      zkConnection: "In ZK systems, the prover executes these stack operations and generates a proof. The verifier can check the proof is valid without seeing your private inputs."
    },
    2: {
      title: "LIFO Order & Deterministic Execution",
      content: "Stack operations are deterministic - the same inputs always produce the same output. This property is essential for zero-knowledge proofs to work reliably.",
      zkConnection: "ZK proofs rely on deterministic computation. The verifier must be able to independently verify that your computation was done correctly, which requires consistent, predictable execution."
    },
    3: {
      title: "Memory Operations & Private State",
      content: "Memory allows you to store and retrieve values privately. In privacy-preserving systems, you can compute with stored data without revealing what's stored.",
      zkConnection: "This simulates how ZK systems handle private state - you can store secrets in memory, perform operations on them, and prove properties about them without exposing the secrets themselves."
    },
    4: {
      title: "Conditional Logic & Privacy",
      content: "Conditionals let you prove properties about private data (like 'is this number even?') without revealing the number itself. This is fundamental to privacy-preserving computation.",
      zkConnection: "Zero-knowledge proofs can verify conditional statements about hidden data. For example: proving your age is over 18 without revealing your exact birthdate."
    },
    5: {
      title: "Complex Private Computations",
      content: "Real zero-knowledge applications involve complex calculations over private inputs. This phase shows how multiple operations combine to solve meaningful problems privately.",
      zkConnection: "Modern ZK systems like Miden can prove arbitrarily complex computations. From private DeFi transactions to confidential voting systems - it's all built on these fundamentals."
    },
    6: {
      title: "Managing Multiple Private Values",
      content: "Privacy systems often need to manage multiple secret values simultaneously. Different memory addresses represent different pieces of private information.",
      zkConnection: "In real ZK applications, you might store multiple private inputs (balances, votes, credentials) and prove relationships between them without exposing any individual value."
    },
    7: {
      title: "Private Comparisons",
      content: "Comparing private values is crucial for many privacy applications: age verification, credit scores, auction bids, etc. You can prove a comparison is true without revealing the values.",
      zkConnection: "ZK range proofs and comparison circuits enable privacy-preserving verification. Example: Proving your salary is above a threshold for a loan without revealing your exact salary."
    },
    8: {
      title: "Building Privacy-Preserving Applications",
      content: "This final challenge combines all concepts to solve a real computational problem. This is how actual ZK applications are built - combining basic operations into powerful privacy tools.",
      zkConnection: "You're now ready to understand how ZK systems like Miden enable private smart contracts, confidential transactions, and trustless privacy. Every zkApp is built from these foundations!"
    }
  };

  // Generate random numbers for each phase
  const generateRandomNumbers = () => {
    const newRandomNumbers = {
      1: {
        a: Math.floor(Math.random() * 10) + 1, // 1-10
        b: Math.floor(Math.random() * 10) + 1, // 1-10
        distractor: Math.floor(Math.random() * 10) + 1 // 1-10
      },
      2: {
        a: Math.floor(Math.random() * 5) + 1, // 1-5
        b: Math.floor(Math.random() * 5) + 1, // 1-5
        c: Math.floor(Math.random() * 5) + 1  // 1-5
      },
      3: {
        storeValue: Math.floor(Math.random() * 10) + 1, // 1-10
        addValue: Math.floor(Math.random() * 10) + 1,   // 1-10
        address: 0 // Keep address as 0 for simplicity
      },
      4: {
        checkValue: Math.floor(Math.random() * 20) + 1, // 1-20
        modValue: 2 // Keep mod value as 2 for even/odd check
      },
      5: {
        a: Math.floor(Math.random() * 8) + 3, // 3-10
        b: Math.floor(Math.random() * 8) + 3, // 3-10
        c: Math.floor(Math.random() * 5) + 2  // 2-6
      },
      6: {
        storeValue1: Math.floor(Math.random() * 10) + 1, // 1-10
        storeValue2: Math.floor(Math.random() * 10) + 1, // 1-10
        addValue: Math.floor(Math.random() * 10) + 1,    // 1-10
        address1: 0, // memory address 1
        address2: 1  // memory address 2
      },
      7: {
        checkValue: Math.floor(Math.random() * 30) + 1, // 1-30
        compareValue: Math.floor(Math.random() * 10) + 1, // 1-10
        modValue: 2
      },
      8: {
        a: Math.floor(Math.random() * 5) + 2, // 2-6
        b: Math.floor(Math.random() * 5) + 2, // 2-6
        c: Math.floor(Math.random() * 5) + 2, // 2-6
        d: Math.floor(Math.random() * 5) + 2  // 2-6
      }
    };
    setRandomNumbers(newRandomNumbers);
    return newRandomNumbers;
  };

  // Initialize random numbers on component mount
  React.useEffect(() => {
    if (!hasInitialized.current) {
      generateRandomNumbers();
      hasInitialized.current = true;
    }
  }, []);

  // Load saved progress for Stack Quest (resume from last phase)
  React.useEffect(() => {
    try {
      const saved = getProgress('games', 'stack-quest');
      if (saved && typeof saved.level === 'number') {
        // level represents completed phases, so currentPhase should be the next phase to play
        const completedPhases = saved.level;
        const nextPhase = Math.min(completedPhases + 1, 8);
        setCurrentPhase(nextPhase);
        if (saved.completed) {
          setShowCompletion(true);
        }
      } else {
        // Initialize progress entry if none exists
        updateGameProgress('stack-quest', null, 0, false);
      }
    } catch (_) {
      // no-op if progress context not ready yet
    }
  }, [getProgress, updateGameProgress]);

  // Generate phases based on current random numbers
  const getPhases = () => {
    if (Object.keys(randomNumbers).length === 0) {
      // Generate default numbers if not initialized yet
      const defaultNumbers = {
        1: { a: 2, b: 3, distractor: 1 },
        2: { a: 2, b: 3, c: 4 },
        3: { storeValue: 5, addValue: 2, address: 0 },
        4: { checkValue: 6, modValue: 2 },
        5: { a: 5, b: 7, c: 3 },
        6: { storeValue1: 4, storeValue2: 6, addValue: 2, address1: 0, address2: 1 },
        7: { checkValue: 15, compareValue: 8, modValue: 2 },
        8: { a: 3, b: 4, c: 5, d: 6 }
      };
      return generatePhasesFromNumbers(defaultNumbers);
    }
    
    const nums = randomNumbers;
    return generatePhasesFromNumbers(nums);
  };

  const generatePhasesFromNumbers = (nums) => {
    return {
    1: {
      title: "Phase 1: Fix a Broken Balance",
        objective: `A payment failed but tokens were deducted. Which private action restores the correct balance?`,
        description: `An account balance is wrong after a failed payment. The account should have the correct total, but the balance shows an error. Choose the private actions that fix it without revealing your transaction history. The invisible math will compute the correct balance.`,
        expectedResult: nums[1].a + nums[1].b,
      availableBlocks: [
          { id: 'push-2', type: 'push', value: nums[1].a, label: 'push first amount' },
          { id: 'push-3', type: 'push', value: nums[1].b, label: 'push second amount' },
        { id: 'add', type: 'add', label: 'combine amounts' },
          { id: 'push-1', type: 'push', value: nums[1].distractor, label: 'push wrong amount' }, // distractor
        { id: 'sub', type: 'sub', label: 'subtract (wrong action)' }, // distractor
      ]
    },
    2: {
      title: "Phase 2: Recover from Multiple Failed Transactions",
        objective: `Multiple transactions failed. Which private action sequence fixes the account state?`,
        description: `An account has multiple failed transactions that left the balance incorrect. You need to recover the correct state using private actions. The VM will run invisible math to compute the fix without exposing each transaction detail.`,
        expectedResult: nums[2].a * (nums[2].b + nums[2].c),
      availableBlocks: [
          { id: 'push-2', type: 'push', value: nums[2].a, label: 'push base amount' },
          { id: 'push-3', type: 'push', value: nums[2].b, label: 'push first adjustment' },
          { id: 'push-4', type: 'push', value: nums[2].c, label: 'push second adjustment' },
        { id: 'add', type: 'add', label: 'combine adjustments' },
        { id: 'mul', type: 'mul', label: 'apply multiplier' },
        { id: 'sub', type: 'sub', label: 'subtract (wrong action)' }, // distractor
      ]
    },
    3: {
      title: "Phase 3: Restore Balance from Backup",
        objective: `Your device was lost. Which recovery path restores your balance without revealing the stored amount?`,
        description: `Your device was lost and you need to recover your account balance from a private backup. Choose the private actions that restore the balance from memory without revealing how much was stored. The recovery proof stays private.`,
        expectedResult: nums[3].storeValue + nums[3].addValue,
      availableBlocks: [
          { id: 'push-5', type: 'push', value: nums[3].storeValue, label: 'push stored balance' },
          { id: 'push-2', type: 'push', value: nums[3].addValue, label: 'push recovery amount' },
          { id: 'push-0', type: 'push', value: nums[3].address, label: 'push memory address' }, // memory address
        { id: 'store', type: 'store', label: 'store in memory' },
        { id: 'load', type: 'load', label: 'load from memory' },
        { id: 'add', type: 'add', label: 'combine amounts' },
      ]
    },
    4: {
      title: "Phase 4: Verify Balance Without Revealing It",
        objective: `The network needs to verify your balance is valid, but you don't want to reveal it. Which private action proves validity?`,
        description: `The network needs to verify your account balance follows the rules (meets certain conditions), but you want to keep the exact amount private. Choose the private actions that prove the balance is valid without revealing what it is.`,
        expectedResult: nums[4].checkValue % 2 === 0 ? 1 : 0, // 1 if even, 0 if odd
      availableBlocks: [
          { id: 'push-6', type: 'push', value: nums[4].checkValue, label: 'push balance to verify' },
          { id: 'push-2', type: 'push', value: nums[4].modValue, label: 'push divisor' },
        { id: 'push-0', type: 'push', value: 0, label: 'push zero' },
        { id: 'mod', type: 'mod', label: 'check remainder' },
        { id: 'eq', type: 'eq', label: 'check equality' },
      ]
      },
      5: {
        title: "Phase 5: Fix Account After Complex Transaction Failure",
        objective: `A complex transaction failed. Which private action sequence fixes the account without revealing transaction details?`,
        description: `A complex transaction involving multiple steps failed partway through, leaving your account in an inconsistent state. Choose the private actions that fix the account state. The invisible math will compute the correction without exposing the failed transaction details.`,
        expectedResult: (nums[5].a + nums[5].b) * nums[5].c,
        availableBlocks: [
          { id: 'push-2', type: 'push', value: nums[5].a, label: 'push first component' },
          { id: 'push-3', type: 'push', value: nums[5].b, label: 'push second component' },
          { id: 'push-4', type: 'push', value: nums[5].c, label: 'push multiplier' },
          { id: 'add', type: 'add', label: 'combine components' },
          { id: 'mul', type: 'mul', label: 'apply multiplier' },
          { id: 'sub', type: 'sub', label: 'subtract (wrong action)' }, // distractor
        ]
      },
      6: {
        title: "Phase 6: Merge Two Accounts Privately",
        objective: `You want to merge two accounts. Which private actions combine them without revealing individual balances?`,
        description: `You have two accounts that need to be merged into one. Choose the private actions that combine the balances without revealing how much was in each account. The merge proof shows the result is valid without exposing the individual amounts.`,
        expectedResult: nums[6].storeValue1 + nums[6].storeValue2,
        availableBlocks: [
          { id: 'push-5', type: 'push', value: nums[6].storeValue1, label: 'push first account balance' },
          { id: 'push-2', type: 'push', value: nums[6].storeValue2, label: 'push second account balance' },
          { id: 'push-0', type: 'push', value: nums[6].address1, label: 'push first address' },
          { id: 'push-1', type: 'push', value: nums[6].address2, label: 'push second address' },
          { id: 'store', type: 'store', label: 'store in memory' },
          { id: 'load', type: 'load', label: 'load from memory' },
          { id: 'add', type: 'add', label: 'combine balances' },
        ]
      },
      7: {
        title: "Phase 7: Prove You Meet a Requirement",
        objective: `You need to prove your balance meets a minimum without revealing it. Which private action creates this proof?`,
        description: `You need to prove your account balance meets a minimum threshold (like for a loan or service), but you don't want to reveal your exact balance. Choose the private actions that prove you meet the requirement while keeping your balance private.`,
        expectedResult: nums[7].checkValue > nums[7].compareValue ? 1 : 0,
        availableBlocks: [
          { id: 'push-6', type: 'push', value: nums[7].checkValue, label: 'push your balance' },
          { id: 'push-2', type: 'push', value: nums[7].compareValue, label: 'push minimum threshold' },
          { id: 'push-0', type: 'push', value: 0, label: 'push zero' },
          { id: 'sub', type: 'sub', label: 'compare difference' },
          { id: 'eq', type: 'eq', label: 'check equality' },
          { id: 'mod', type: 'mod', label: 'check remainder (wrong)' }, // distractor
        ]
      },
      8: {
        title: "Phase 8: Fix Ledger with Multiple Accounts",
        objective: `A ledger has multiple accounts with errors. Which private actions fix the ledger state without revealing individual account balances?`,
        description: `A ledger has multiple accounts that need fixing after a system error. You need to prove the ledger state is correct across all accounts, but you don't want to reveal individual account balances. Choose the private actions that fix the ledger. The invisible math will verify the state without exposing each account's details.`,
        expectedResult: Math.floor((nums[8].a + nums[8].b + nums[8].c + nums[8].d) / 4),
        availableBlocks: [
          { id: 'push-2', type: 'push', value: nums[8].a, label: 'push account 1 balance' },
          { id: 'push-3', type: 'push', value: nums[8].b, label: 'push account 2 balance' },
          { id: 'push-4', type: 'push', value: nums[8].c, label: 'push account 3 balance' },
          { id: 'push-5', type: 'push', value: nums[8].d, label: 'push account 4 balance' },
          { id: 'push-0', type: 'push', value: 0, label: 'push zero' },
          { id: 'push-1', type: 'push', value: 1, label: 'push one' },
          { id: 'add', type: 'add', label: 'combine balances' },
          { id: 'mul', type: 'mul', label: 'multiply' },
          { id: 'div', type: 'div', label: 'divide by count' },
          { id: 'store', type: 'store', label: 'store in memory' },
          { id: 'load', type: 'load', label: 'load from memory' },
        ]
      }
    };
  };

  const phases = getPhases();

  const executeProgram = () => {
    setIsRunning(true);
    setShowResult(false);
    setCurrentStep(0);
    
    let tempStack = [];
    let tempMemory = {};
    let steps = [];
    
    // Add initial state
    steps.push({
      instruction: 'Start',
      stack: [...tempStack],
      memory: {...tempMemory},
      description: 'Program execution begins'
    });

    // Execute each block
    programBlocks.forEach((block, index) => {
      switch (block.type) {
        case 'push':
          tempStack.push(block.value);
          steps.push({
            instruction: `${block.label}`,
            stack: [...tempStack],
            memory: {...tempMemory},
            description: `The value stays private.`
          });
          break;
          
        case 'add':
          if (tempStack.length >= 2) {
            const b = tempStack.pop();
            const a = tempStack.pop();
            const result = a + b;
            tempStack.push(result);
            steps.push({
              instruction: 'add',
              stack: [...tempStack],
              memory: {...tempMemory},
              description: `Combined the amounts. The invisible math computed the result.`
            });
          }
          break;
          
        case 'mul':
          if (tempStack.length >= 2) {
            const b = tempStack.pop();
            const a = tempStack.pop();
            const result = a * b;
            tempStack.push(result);
            steps.push({
              instruction: 'mul',
              stack: [...tempStack],
              memory: {...tempMemory},
              description: `Applied the multiplier. The invisible math computed the result.`
            });
          }
          break;
          
        case 'sub':
          if (tempStack.length >= 2) {
            const b = tempStack.pop();
            const a = tempStack.pop();
            const result = a - b;
            tempStack.push(result);
            steps.push({
              instruction: 'sub',
              stack: [...tempStack],
              memory: {...tempMemory},
              description: `Compared the difference. The invisible math computed the result.`
            });
          }
          break;
          
        case 'div':
          if (tempStack.length >= 2) {
            const b = tempStack.pop();
            const a = tempStack.pop();
            const result = Math.floor(a / b);
            tempStack.push(result);
            steps.push({
              instruction: 'div',
              stack: [...tempStack],
              memory: {...tempMemory},
              description: `Divided by count. The invisible math computed the result.`
            });
          }
          break;
          
        case 'store':
          if (tempStack.length >= 2) {
            const address = tempStack.pop();
            const value = tempStack.pop();
            tempMemory[address] = value;
            steps.push({
              instruction: 'store',
              stack: [...tempStack],
              memory: {...tempMemory},
              description: `Stored the value in memory. The amount stays private.`
            });
          }
          break;
          
        case 'load':
          if (tempStack.length >= 1) {
            const address = tempStack.pop();
            const value = tempMemory[address] || 0;
            tempStack.push(value);
            steps.push({
              instruction: 'load',
              stack: [...tempStack],
              memory: {...tempMemory},
              description: `Loaded the value from memory. The private amount is restored.`
            });
          }
          break;
          
        case 'mod':
          if (tempStack.length >= 2) {
            const b = tempStack.pop();
            const a = tempStack.pop();
            const result = a % b;
            tempStack.push(result);
            steps.push({
              instruction: 'mod',
              stack: [...tempStack],
              memory: {...tempMemory},
              description: `Checked the remainder. The invisible math verified the condition.`
            });
          }
          break;
          
        case 'eq':
          if (tempStack.length >= 2) {
            const b = tempStack.pop();
            const a = tempStack.pop();
            const result = a === b ? 1 : 0;
            tempStack.push(result);
            steps.push({
              instruction: 'eq',
              stack: [...tempStack],
              memory: {...tempMemory},
              description: `Checked equality. The invisible math verified: ${result === 1 ? 'condition met' : 'condition not met'}`
            });
          }
          break;
          
        default:
          break;
      }
    });

    setExecutionSteps(steps);
    setStack(tempStack);
    
    
    // Auto-play through steps
    let stepIndex = 0;
    const interval = setInterval(() => {
      setCurrentStep(stepIndex);
      stepIndex++;
      
      if (stepIndex >= steps.length) {
        clearInterval(interval);
        setIsRunning(false);
        setShowResult(true);
      }
    }, 1000);
  };

  const handleDragStart = (e, block) => {
    setDraggedBlock(block);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedBlock) {
      setProgramBlocks([...programBlocks, draggedBlock]);
      setDraggedBlock(null);
    }
  };

  const removeBlock = (index) => {
    setProgramBlocks(programBlocks.filter((_, i) => i !== index));
  };

  const clearProgram = () => {
    setProgramBlocks([]);
    setStack([]);
    setExecutionSteps([]);
    setCurrentStep(0);
    setShowResult(false);
  };

  const nextPhase = () => {
    if (currentPhase < 8) {
      // Mark current phase as completed before moving to next
      updateGameProgress('stack-quest', null, currentPhase, false);
      const newPhase = currentPhase + 1;
      setCurrentPhase(newPhase);
      clearProgram();
      // Generate new random numbers for the new phase
      generateRandomNumbers();
    } else if (currentPhase === 8) {
      // Mark phase 8 as completed
      updateGameProgress('stack-quest', null, 8, true, 'Account Recovery');
      // Show completion screen after phase 8
      setShowCompletion(true);
    }
  };


  const currentPhaseData = phases[currentPhase];
  const currentExecutionStep = executionSteps[currentStep];

  // Don't render if phases aren't initialized yet
  if (!currentPhaseData) {
    return <div className="stack-quest">Loading...</div>;
  }

  // Show completion screen
  if (showCompletion) {
    return (
      <div className="stack-quest">
        <div className="completion-screen">
          <div className="completion-content">
            <div className="completion-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
              </svg>
            </div>
            <h1>Fix the Ledger Complete!</h1>
            <p className="completion-subtitle">You've mastered the fundamentals of stack-based programming</p>
            
            <div className="achievements">
              <h3>What you've learned:</h3>
              <ul>
                <li><span className="achievement-indicator">→</span> <strong>Stack Operations:</strong> Understanding LIFO (Last In, First Out) behavior</li>
                <li><span className="achievement-indicator">→</span> <strong>Basic Arithmetic:</strong> Addition and multiplication on the stack</li>
                <li><span className="achievement-indicator">→</span> <strong>Memory Management:</strong> Storing and loading values from memory</li>
                <li><span className="achievement-indicator">→</span> <strong>Conditional Logic:</strong> Using modulo and comparison operations</li>
                <li><span className="achievement-indicator">→</span> <strong>Advanced Arithmetic:</strong> Complex calculations with multiple operations</li>
                <li><span className="achievement-indicator">→</span> <strong>Multiple Memory:</strong> Managing multiple memory addresses</li>
                <li><span className="achievement-indicator">→</span> <strong>Advanced Conditionals:</strong> Complex comparison and branching logic</li>
                <li><span className="achievement-indicator">→</span> <strong>Real-World Problem Solving:</strong> Combining all concepts for practical applications</li>
              </ul>
            </div>

            <div className="zk-achievement" style={{ marginTop: '24px', padding: '16px', background: 'rgba(249,115,22,0.1)', borderRadius: '8px', border: '1px solid rgba(249,115,22,0.3)' }}>
              <h3 style={{ color: '#f97316', marginTop: 0 }}>You're Now Ready for Zero-Knowledge!</h3>
              <p style={{ color: '#e2e8f0', margin: '8px 0' }}>
                You've learned the fundamentals of stack-based computation that power zero-knowledge virtual machines like Miden. 
                These concepts enable privacy-preserving applications across DeFi, identity, voting, and more.
              </p>
              <p style={{ color: '#f97316', marginBottom: 0 }}>
                <strong>Next Steps:</strong> Explore the Miden tutorials to learn how to write actual zero-knowledge programs and generate real proofs!
              </p>
            </div>

            <div className="completion-actions">
              <button 
                onClick={() => {
                  setShowCompletion(false);
                  setCurrentPhase(1);
                  clearProgram();
                  generateRandomNumbers();
                }}
                className="restart-btn"
              >
                Play Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stack-quest">
      {showIntro && (
        <div className="sq-modal-overlay" role="dialog" aria-modal="true">
          <div className="sq-modal">
            <div className="sq-modal-header">
              <h2>Fix the Ledger</h2>
              <button className="sq-close" onClick={() => setShowIntro(false)} aria-label="Close">×</button>
            </div>
            <div className="sq-modal-body">
              <p style={{ marginBottom: '14px' }}>Fix the Ledger teaches how stack-based virtual machines work. Build programs using stack operations (push, add, mul), memory (store, load), and logic (eq, mod). Each phase introduces new operations to solve progressively harder challenges. Learn the fundamentals that power Miden's zero-knowledge execution.</p>
              
              <div style={{ background: 'rgba(249,115,22,0.1)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(249,115,22,0.3)', marginBottom: '12px' }}>
                <h4 style={{ color: '#f97316', marginTop: 0, marginBottom: '6px', fontSize: '13px' }}>What You'll Learn:</h4>
                <ul style={{ margin: 0, paddingLeft: '18px', color: '#e2e8f0', fontSize: '12px' }}>
                  <li style={{ marginBottom: '4px' }}>How stack-based virtual machines work</li>
                  <li style={{ marginBottom: '4px' }}>Why deterministic computation enables zero-knowledge proofs</li>
                  <li style={{ marginBottom: '4px' }}>How to compute with private data without revealing it</li>
                  <li style={{ marginBottom: '4px' }}>The building blocks of privacy-preserving applications</li>
                </ul>
              </div>

              <div className="sq-examples">
                <div className="sq-example">
                  <h4>Phase 1: Push & Add</h4>
                  <pre>
push 2\npush 3\nadd  → 5
                  </pre>
                  <p>Push values onto the stack, then combine the top two items with <code>add</code>.</p>
                </div>
                <div className="sq-example">
                  <h4>Phase 2: Multiplication</h4>
                  <pre>
push 2\npush 3\npush 4\nadd\nmul  → 14
                  </pre>
                  <p>Remember: the stack is LIFO. <code>add</code> combines the last two values first.</p>
                </div>
                <div className="sq-example">
                  <h4>Phase 3: Memory</h4>
                  <pre>
push 5\npush 0\nstore\npush 0\nload\npush 2\nadd  → 7
                  </pre>
                  <p>Use <code>store</code> and <code>load</code> with an address (e.g., 0) to persist values.</p>
                </div>
                <div className="sq-example">
                  <h4>Phase 4: Conditionals</h4>
                  <pre>
push 6\npush 2\nmod\npush 0\neq  → 1 (true)
                  </pre>
                  <p><code>mod</code> gets the remainder. Compare to 0 with <code>eq</code> to check if even.</p>
                </div>
              </div>
            </div>
            <div className="sq-modal-footer">
              <button className="sq-primary" onClick={() => setShowIntro(false)}>Start Building →</button>
            </div>
          </div>
        </div>
      )}
      <div className="quest-header">
        <div className="phase-info">
          <h1>{currentPhaseData.title}</h1>
          <div className="phase-meta">
            <span className="phase-number">Phase {currentPhase} of 8</span>
            <span className={`difficulty-badge ${currentPhase <= 4 ? 'easy' : 'medium'}`}>
              {currentPhase <= 4 ? 'EASY' : 'MEDIUM'}
            </span>
          </div>
        </div>
        <div className="instruction-box">
          <p className="objective">{currentPhaseData.objective}</p>
          <p className="description">{currentPhaseData.description}</p>
        </div>
      </div>

      <div className="quest-content">
        {/* Available Blocks */}
        <div className="blocks-palette">
          <h3>Available Blocks</h3>
          <div className="blocks-grid">
            {currentPhaseData.availableBlocks.map((block) => (
              <div
                key={block.id}
                className="block"
                draggable
                onDragStart={(e) => handleDragStart(e, block)}
              >
                <span>{block.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Program Area */}
        <div className="program-area">
          <h3>Your Program</h3>
          <div
            className="program-box"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {programBlocks.length === 0 ? (
              <div className="empty-program">Drag blocks here to build your program</div>
            ) : (
              programBlocks.map((block, index) => (
                <div key={`${block.id}-${index}`} className="program-block">
                  <span>{block.label}</span>
                  <button onClick={() => removeBlock(index)}>×</button>
                </div>
              ))
            )}
          </div>
          
          <div className="program-controls">
            <button onClick={executeProgram} disabled={isRunning || programBlocks.length === 0}>
              {isRunning ? 'Running...' : 'Run Program'}
            </button>
            <button onClick={clearProgram}>Clear</button>
          </div>
        </div>

        {/* Stack Visualization */}
        <div className="stack-visualization">
          <h3>Stack State</h3>
          <div className="stack-display">
            {currentExecutionStep ? (
              <>
                <div className="stack-items">
                  {currentExecutionStep.stack.length === 0 ? (
                    <div className="empty-stack">Empty</div>
                  ) : (
                    <>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px', fontStyle: 'italic' }}>
                        Stack (invisible math - values are hidden)
                      </div>
                      {[...currentExecutionStep.stack]
                        .slice()
                        .reverse()
                        .map((item, index) => (
                          <div key={`stack-${index}-${item}`} className="stack-item">
                            <span>●</span>
                          </div>
                        ))}
                    </>
                  )}
                </div>
                <div className="execution-info">
                  <p><strong>Instruction:</strong> {currentExecutionStep.instruction}</p>
                  <p><strong>Description:</strong> {currentExecutionStep.description}</p>
                  {Object.keys(currentExecutionStep.memory).length > 0 && (
                    <p><strong>Memory:</strong> Private values stored (hidden)</p>
                  )}
                </div>
              </>
            ) : (
              <div className="empty-stack">Ready to execute</div>
            )}
          </div>

          {/* Result moved to bottom of Stack State panel */}
          {showResult && (
            <div className="result-panel" style={{ marginTop: '-20px' }}>
              <h3>Result</h3>
              <div className="result-value" style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                {stack.length > 0 ? '(hidden)' : 'No result'}
              </div>
              {stack.length > 0 && stack[stack.length - 1] === currentPhaseData.expectedResult ? (
                <div className="success">
                  <p>Correct! The account was fixed using private actions. Well done!</p>
                  {currentPhase < 8 ? (
                    <button onClick={nextPhase} className="next-phase-btn">
                      Next Phase
                    </button>
                  ) : (
                    <button onClick={nextPhase} className="next-phase-btn">
                      Complete Quest
                    </button>
                  )}
                </div>
              ) : (
                <div className="error">
                  <p>The account state doesn't match. Try a different sequence of private actions!</p>
                  <p>Remember: choose which actions fix the problem, not the numbers themselves.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Knowledge Box with ZK Context */}
      <div className="knowledge-box">
        <h3>
          <svg className="knowledge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4"/>
            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
            <path d="M12 2a7 7 0 0 1 7 7c0 1.5-.5 2.9-1.3 4"/>
            <path d="M12 2a7 7 0 0 0-7 7c0 1.5.5 2.9 1.3 4"/>
          </svg>
          {phaseExplanations[currentPhase].title}
        </h3>
        <p style={{ marginBottom: '12px' }}>{phaseExplanations[currentPhase].content}</p>
        <div style={{ padding: '12px', background: 'rgba(249,115,22,0.1)', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.3)', marginTop: '12px' }}>
          <div style={{ fontWeight: 600, color: '#f97316', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            Zero-Knowledge Connection
          </div>
          <p style={{ margin: 0, color: '#e2e8f0', fontSize: '14px' }}>{phaseExplanations[currentPhase].zkConnection}</p>
        </div>
      </div>

      {isDevMode && (
        <UnderTheHood>
          <p><strong>Miden VM Stack Architecture:</strong></p>
          <p>The blocks you're dragging represent Miden VM assembly instructions:</p>
          <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
            <li><strong>push:</strong> Pushes a value onto the stack</li>
            <li><strong>add/mul/sub:</strong> Arithmetic operations that pop two values, compute, and push result</li>
            <li><strong>store:</strong> Stores stack value to memory at specified address</li>
            <li><strong>load:</strong> Loads value from memory address onto stack</li>
            <li><strong>eq/mod:</strong> Comparison and modulo operations</li>
          </ul>
          <p style={{ marginTop: '12px' }}>
            <strong>Stack-based Execution:</strong> Miden VM uses a stack-based architecture where operations 
            consume values from the top of the stack and push results back. This deterministic execution 
            enables zero-knowledge proofs - you can prove the computation was correct without revealing inputs.
          </p>
          <p style={{ marginTop: '8px' }}>
            <strong>Current Phase ({currentPhase}):</strong> {currentPhaseData.objective}
          </p>
          <p style={{ marginTop: '8px', fontSize: '13px', color: '#94a3b8' }}>
            The stack visualization above shows the actual state of the Miden VM stack during execution.
          </p>
        </UnderTheHood>
      )}

      {/* Result panel moved above into Stack State */}
    </div>
  );
};

export default StackQuest;
